import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BackendObjectRef } from '../lib/backend-integration.service';

import { FormField } from '../lib/form-field.model';
import { FormFieldType, FormFieldTypeGeneral, FormFieldTypeUserField } from '../lib/form-field-type.model';;
import { FormPermission } from '../lib/form-permission.model';
import { FUIValueListItem } from '@fry/ui-material/value-list-control';

export interface WorkflowFormFieldFormOptions {
  /**
   * Known Form field Types user can select from
   */
  fieldTypes: FormFieldType[];

  /**
   * Form field which is being edited or added (when undefined)
   */
  item: FormField|undefined;

  /**
   * Permission for the Form field which is edited
   */
  permissions: FormPermission[];

  /**
   * List of Form fields defined for the Workflow, includes the `item`
   */
  fields: FormField[];

  /**
   * List of available roles
   */
  availableRoles: BackendObjectRef[];
}

export type WorkflowFormFieldFormResult = FormField|string|undefined;

interface WorkflowFormFieldFormValue {
  name: any;
  type: any;
  required: any;
  visibility: any;
  availableValues?: any;
  roles?: any;
  content?: any;
}
type WorkflowFormFieldFormControls = WorkflowFormFieldFormValue;

@Component({
    selector: 'eas-workflow-form-field-form',
    templateUrl: './form-field-form.component.html',
    styleUrls: ['./form-field-form.component.css'],
    standalone: false
})
export class WorkflowFormFieldFormComponent implements OnDestroy {
  public readonly form: FormGroup;
  public readonly fieldTypes: FormFieldType[];
  public readonly fieldPermissions: FormPermission[];
  public readonly availableFields: FormField[];
  public readonly availableRoles: BackendObjectRef[];
  public          selectedType?: FormFieldType;

  public readonly pluralMapping: Record<string, object> = {
    'role':  { '=1' : '1 role', 'other' : '# roles' },
    'state': { '=1' : '1 state', 'other' : '# states' },
  };

  private readonly original?: FormField;
  public           result?: FormField;
  private readonly dialogRef: MatDialogRef<WorkflowFormFieldFormOptions, WorkflowFormFieldFormResult>;
  private readonly destroyed$ = new Subject<void>();

  constructor(fb: FormBuilder,
              @Inject(MAT_DIALOG_DATA) data: WorkflowFormFieldFormOptions,
              dialogRef: MatDialogRef<WorkflowFormFieldFormOptions, WorkflowFormFieldFormResult>) {
    this.dialogRef = dialogRef;
    this.fieldTypes = data.fieldTypes;
    this.fieldPermissions = data.permissions;
    this.result = this.original = data.item;
    this.availableFields
      = data.fields
      ? data.fields.filter(field => field.id !== data.item?.id)
      : data.fields;

    this.availableRoles = data.availableRoles;

    this.form = fb.group(this.formControlsFromItem(this.result));
    this.selectedType = this.result?.type;

    this.form.valueChanges
             .pipe(takeUntil(this.destroyed$))
             .subscribe(value => {
               try {
                 this.result = this.formValueToItem(this.result, value);

                 // Adjust validity of the Name field
                 const nameControl = this.form.get('name') as FormControl;
                 const validators = this.shouldCaptureName ? [Validators.required] : null;
                 if (!this.shouldCaptureName) { nameControl.setValue('', {emitEvent: false}); }
                 nameControl.setValidators(validators);
                 nameControl.updateValueAndValidity();
               } catch (_error) {
                 // We can ignore errors here as we just update the UI
               }
             });

    (this.form.get('type') as FormControl)
      .valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(typeID => {
        const previousSelectedTypeName = this.selectedType?.name;
        this.selectedType = this.fieldTypes.find(ft => ft.id === typeID);

        // Handle resetting the value of the name field
        if (this.selectedType?.isUserField && this.form.value.name === '') {
          this.form.patchValue({name: this.selectedType.name});
        }

        if (this.selectedType?.isUserField && this.form.value.name === previousSelectedTypeName) {
          this.form.patchValue({name: this.selectedType.name});
        }
      });
  }

  //
  // Lifecycle
  //

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private formControlsFromItem(item: FormField|undefined): WorkflowFormFieldFormControls {
    const controls: WorkflowFormFieldFormControls = {
      name: [item?.name ?? ''],
      type: [item?.type.id ?? '', Validators.required],
      required: [item?.required ?? false],
      visibility: [item?.visibility ? { enabled: true, ...item.visibility } : { enabled: false }],
    };
    if (this.shouldCaptureName) { controls.name.push(Validators.required); }

    if (item?.type.isDiscrete && item.availableValues) {
      const values: FUIValueListItem[] = item.availableValues.map(v => {
        return { key: v.id, value: v.name};
      });
      controls.availableValues = [values];
    } else {
      controls.availableValues = [];
    }

    if (item?.type.isAssignee) {
      controls.roles = [item.roles || []];
    } else {
      controls.roles = [];
    }

    if (item?.type.isContentRequired) {
      controls.content = [item.content, Validators.required];
    } else {
      controls.content = [''];
    }

    return controls;
  }

  private formValueToItem(item: FormField|undefined,
                          value: WorkflowFormFieldFormValue): FormField {
    const type = this.fieldTypes.find(ft => ft.id === value.type);
    if (!type) { throw new Error($localize `Unable to find field type '${value.type}'!`); }

    const availableValues: BackendObjectRef[]
      = type.isDiscrete && value.availableValues
        ? value.availableValues.map((i: FUIValueListItem) => {
          return { id: i.key, name: i.value };
        })
        : undefined;

    const content: string = type.isContentRequired && value.content
                            ? value.content
                            : undefined;

    return new FormField(item?.id,
                         value.name,
                         type,
                         value.required,
                         value.visibility.enabled ? value.visibility : undefined,
                         availableValues,
                         value.roles,
                         content,
                         item?.asyncValidators,
                         item?.syncValidators);
  }

  //
  // Actions
  //

  public onSave(): void {
    const result = this.formValueToItem(this.result, this.form.value);
    this.dialogRef.close(result);
  }

  public onCancel(): void {
    this.dialogRef.close(undefined);
  }

  public onDelete(): void {
    if (!this.result) { return; }
    this.dialogRef.close(this.result.id);
  }

  //
  // Template helpers
  //

  public get isAdding(): boolean {
    return !this.original;
  }

  public get shouldShowDelete(): boolean {
    return !this.isAdding;
  }

  public get shouldShowSave(): boolean {
    return !this.isAdding;
  }

  public get shouldCaptureName(): boolean {
    if (this.form === undefined) { return true; }
    const type = this.fieldTypes.find(ft => ft.id === this.form.value.type);
    if (!type) { return true; }
    return type.isUserEditable;
  }

  public get shouldAllowManageSelectOptions(): boolean {
    if (!this.selectedType) { return false; }
    return this.selectedType.isDiscrete && !this.selectedType.isUserField;
  }

  public get allowManageRoles(): boolean {
    if (!this.selectedType) { return false; }
    return this.selectedType.isAssignee;
  }

  public get isUsed(): boolean {
    return this.fieldPermissions.length > 0;
  }

  public get numberOfRoles(): number {
    const items: {[K: string]: any} = {};
    this.fieldPermissions.forEach(p => items[p.role] = true);
    return Object.keys(items).length;
  }

  public get numberOfStates(): number {
    const items: {[K: string]: any} = {};
    this.fieldPermissions.forEach(p => items[p.state] = true);
    return Object.keys(items).length;
  }

  public get basicFields(): FormFieldTypeGeneral[] {
    return this.fieldTypes.filter(f => f instanceof FormFieldTypeGeneral)
                          .sort((a, b) => a.name.localeCompare(b.name));
  }

  public get userFields(): FormFieldTypeUserField[] {
    return this.fieldTypes.filter(f => f.isUserField)
                          .sort((a, b) => a.name.localeCompare(b.name));
  }

  public conditionalVisiblityFields(): FormField[] {
    return this.availableFields.filter(f => f.type.isUserEditable);
  }

  public get dialogTitle(): string {
    return this.isAdding ? $localize `ADD NEW FORM FIELD` : `EDIT FORM FIELD`;
  }

  public get submitButtonTitle(): string {
    return this.isAdding ? $localize `Add` : `Apply`;
  }

  public fieldValidatorsDescription(): string|undefined {
    if (!this.result) { return undefined; }
    if (this.result.asyncValidators.length === 0 &&
        this.result.syncValidators.length === 0) {
          return undefined;
          // return $localize `No custom validators defined for this field.`;
        }

    return [
      ...this.result.asyncValidators,
      ...this.result.syncValidators
    ].join(', ')
  }
}
